import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Linkedin, Menu, X } from "lucide-react";
import { LINKEDIN_URL, NAV_LINKS } from "../content";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "border-b border-ink/10 bg-ivory/90 backdrop-blur-md" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-[4.25rem] sm:px-8">
        <a href="#top" data-testid="site-logo" className="group flex items-center gap-3" aria-label="Anjali S. Menon, back to top">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-cherry font-display text-xs text-gold transition-colors duration-300 group-hover:bg-cherry-deep sm:h-9 sm:w-9 sm:text-sm">
            AM
          </span>
          <span className="leading-tight">
            <span className="block font-display text-sm font-semibold text-ink sm:text-base">Anjali S. Menon</span>
            <span className="block text-[9px] font-medium uppercase tracking-[0.2em] text-ink/55 sm:text-[10px]">
              AML &amp; Compliance Consultancy
            </span>
          </span>
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-6 lg:flex">
          {NAV_LINKS.slice(0, 5).map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-medium text-ink/70 underline-offset-4 transition-colors duration-200 hover:text-cherry hover:underline"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-contact-cta"
            className="rounded-full bg-cherry px-5 py-2 text-sm font-medium text-ivory transition-colors duration-200 hover:bg-cherry-deep"
          >
            Get in Touch
          </a>
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          className="flex h-10 w-10 items-center justify-center rounded-md text-ink lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            data-testid="mobile-menu"
            aria-label="Mobile"
            initial={reduce ? false : { opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="absolute inset-x-0 top-full border-b border-ink/10 bg-ivory shadow-lg lg:hidden"
          >
            <ul className="space-y-1 px-5 py-5">
              {NAV_LINKS.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={reduce ? false : { opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                >
                  <a
                    href={`#${l.id}`}
                    data-testid={`mobile-nav-link-${l.id}`}
                    onClick={() => setOpen(false)}
                    className="block py-2 font-display text-xl text-ink transition-colors hover:text-cherry"
                  >
                    {l.label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="grain relative bg-cherry-deep text-ivory/85">
      <div className="relative z-[2] mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-16">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-ivory/10 font-display text-sm text-gold">
                AM
              </span>
              <span className="font-display text-lg font-semibold text-ivory">Anjali S. Menon</span>
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-ivory/65">
              Independent Legal &amp; Compliance Consultancy
            </p>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin-link"
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-gold underline-offset-4 transition-colors hover:text-ivory hover:underline"
            >
              <Linkedin className="h-4 w-4" aria-hidden="true" />
              Connect on LinkedIn
            </a>
          </div>

          <nav aria-label="Footer">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-gold">Explore</p>
            <ul className="mt-4 space-y-2">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <a
                    href={`#${l.id}`}
                    data-testid={`footer-nav-link-${l.id}`}
                    className="text-sm text-ivory/70 underline-offset-4 transition-colors hover:text-ivory hover:underline"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-gold">Professional information</p>
            <p className="mt-4 text-xs leading-relaxed text-ivory/55">
              Anjali S. Menon provides independent legal operations, commercial contracts and regulatory compliance
              consultancy services. Services are subject to applicable professional and regulatory requirements and
              do not include reserved legal activities where authorisation is required.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-ivory/55">
              Information on this website is provided for general purposes and does not constitute legal advice.
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-ivory/15 pt-6 text-xs text-ivory/50 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Anjali S. Menon. All rights reserved.</p>
          <p>Independent Legal &amp; Compliance Consultancy</p>
        </div>
      </div>
    </footer>
  );
}
